import { Internalisation, Point, Stage } from "./internalisation";
import { DataEntityInterface, EmittableMessageData, Link, LinkGroup, MessageData, Message as MessageInterface, Thread, ThreadData, WithOptionalProperties } from "./types";

export function generateId(): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const length = 25;
  let result = '';
  
  for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
  }
  
  return result;
}

export class StreamBuffer {
  private buffer: string = '';
  public static readonly delimiter: string = "__END__";

  add(chunk: string): void {
    this.buffer += chunk;
  }

  flush(): Array<EmittableMessageData[]> {
    const messages: Array<EmittableMessageData[]> = [];
    const parts = this.buffer.split(StreamBuffer.delimiter);
    this.buffer = parts.pop() || '';
    for (const part of parts) {
      try {
        const message = JSON.parse(part) as EmittableMessageData[];
        messages.push(message);
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    }
    return messages;
  }
}

export class Message implements MessageInterface, DataEntityInterface<MessageData> {
  private _links: Array<Link|LinkGroup> = [];
  public internalisation: Internalisation;
  public readonly id: string
  public content: MessageInterface['content']
  public readonly role: MessageInterface['role']
  public status: MessageInterface['status'] = 'in_progress'
  public metadata: MessageInterface['metadata'] = {}
  
  constructor({
    id, content, role, status
  }: Pick<MessageInterface, 'id'|'content'|'role'|'status'>) {
    this.id = id;
    this.content = content;
    this.role = role;
    this.status = status;
    this.internalisation = new Internalisation();
  }

  static fromUserInput(input: string) {
    return new Message({
      id: generateId(),
      content: input,
      role: 'user',
      status: 'completed'
    })
  }

  get links(): MessageInterface['links'] {
    return this._links;
  }

  setContent(content: MessageInterface['content']): this {
    this.content = content;
    return this;
  }

  setLinks(links: Array<Link|LinkGroup>): void {
    this._links = links;
  }

  addLink(link: Link|LinkGroup): this {
    this._links.push(link);
    return this;
  }

  getLinkGroupById(id: string): LinkGroup | undefined {
    const groups = this._links.filter((link) => Object.hasOwn(link, 'id')) as LinkGroup[];
    return groups.find((link) => link.id === id);
  }

  addMetadata(key: string, value: any): this {
    if (!this.metadata) {
      this.metadata = {};
    }
    this.metadata[key] = value;
    return this;
  }

  setStatus(status: MessageInterface["status"]): void {
    this.status = status;
  }

  setMetadata(metadata: { [key: string]: any }): void {
    this.metadata = metadata;
  }

  static fromData(data: WithOptionalProperties<MessageData, 'links' | 'internalisation' | 'metadata'>): Message {
    const message = new Message({
      id: data.id,
      content: data.content,
      role: data.role,
      status: data.status
    });
    if (data.links) {
      message._links = data.links;
    }
    if (data.internalisation) {
      if (data.internalisation.points) {
        data.internalisation.points.forEach((point) => {
          message.internalisation.addPoint(Point.fromData(point));
        });
      }
      if (data.internalisation.trail) {
        data.internalisation.trail.forEach((stage) => {
          message.internalisation.addStage(Stage.fromData(stage));
        });
      }
    }
    if (data.metadata) {
      message.metadata = data.metadata;
    }
    return message;
  }

  toData(): MessageData {
    return {
      id: this.id,
      content: this.content,
      role: this.role,
      status: this.status,
      links: this.links,
      internalisation: this.internalisation.toData(),
      metadata: this.metadata
    }
  }
}

export class PartialThread implements Iterable<Message>, Thread {
  protected _messages: Message[];

  constructor(messages: Message[] = []) {
    this._messages = messages;
  }

  get messages(): Message[] {
    return this._messages;
  }
  get length(): number {  
    return this._messages.length;
  }

  [Symbol.iterator](): Iterator<Message> {
    let index = 0;
    const messages = this._messages;
    return {
      next(): IteratorResult<Message> {
        if (index < messages.length) {
          return { value: messages[index++], done: false };
        } else {
          return { value: null, done: true };
        }
      }
    };
  }

  toData(): ThreadData {
    return {
      messages: this._messages.map(message => message.toData())
    } 
  }

  static fromData(data: ThreadData): PartialThread {
    const thread = new PartialThread();
    thread._messages = data.messages.map(message => Message.fromData(message));
    return thread;
  }

  getMessageById(id: string): Message | undefined {
    return this._messages.find(message => message.id === id);
  }

  addMessage(message: Message): this {
    this._messages.push(message);
    return this;
  }

  setMessage(message: Message): void {
    const index = this._messages.findIndex((msg) => msg.id === message.id);
    if (index === -1) {
      this.addMessage(message);
    }
    else {
      this._messages[index] = message;
    }
  }

  getLastMessage(type: MessageInterface["role"] = 'agent'): MessageInterface | undefined {
    return this._messages.filter(msg => msg.role === type).pop();
  }
}